import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-recording-indicator',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  template: `
    <div class="recording-indicator-container" [class.variant-danger]="variant === 'danger'" [class.variant-primary]="variant === 'primary'">
      <div class="halo" [style.transform]="'scale(' + haloScale() + ')'" [style.opacity]="haloOpacity()"></div>
      <button mat-flat-button class="is-recording" (click)="stopClick.emit()">
        <mat-icon class="icon-filled">{{ icon }}</mat-icon>
      </button>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .recording-indicator-container {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;

      .halo {
        position: absolute;
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background-color: var(--skz-background-content-button);
        transition:
          transform 0.2s ease-out,
          opacity 0.3s ease-out;
        pointer-events: none;
      }

      .is-recording {
        position: relative;
        border-radius: 50% !important;
        padding: 0 !important;
        min-width: 36px;
        line-height: 36px;

        .icon-filled {
          font-variation-settings:
            'FILL' 1,
            'opsz' 20;
        }
      }

      button {
        height: 36px;
        line-height: 36px;
        background-color: var(--skz-highlighted-item-color);
      }

      .mat-icon {
        margin: 0;
        color: var(--skz-background-content-button);
      }

      &.variant-danger {
        .halo {
          background-color: #ef4444;
        }
        button {
          background-color: #fee2e2;
          &:hover {
            background-color: #fca5a5;
          }
        }
        .mat-icon {
          color: #dc2626;
        }
      }
    }
  `],
})
export class RecordingIndicatorComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) stream!: MediaStream | null;
  @Input() icon = 'mic';
  @Input() variant: 'primary' | 'danger' = 'primary';
  @Output() stopClick = new EventEmitter<void>();

  haloScale = signal(1);
  haloOpacity = signal(0.5);

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array<ArrayBuffer> | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animationFrameId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stream'] && this.stream) {
      this.initAudioVisualization();
    } else {
      this.stopVisualization();
    }
  }

  ngOnDestroy(): void {
    this.stopVisualization();
  }

  private initAudioVisualization(): void {
    if (!this.stream || this.audioContext) return;

    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.source.connect(this.analyser);

    this.draw();
  }

  private draw = () => {
    if (!this.analyser || !this.dataArray) return;

    this.animationFrameId = requestAnimationFrame(this.draw);
    this.analyser.getByteFrequencyData(this.dataArray);

    let sum = 0;
    for (const amplitude of this.dataArray) {
      sum += amplitude * amplitude;
    }
    const average = Math.sqrt(sum / this.dataArray.length);

    const normalizedVolume = average / 128;
    // Reduced the multiplier from 0.5 to 0.3 to make the halo effect more subtle.
    const newScale = 1 + normalizedVolume * 0.3;
    const newOpacity = 0.3 + normalizedVolume * 0.4;

    this.haloScale.set(newScale);
    this.haloOpacity.set(Math.min(newOpacity, 1));
  };

  private stopVisualization(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.source?.disconnect();
    this.source = null;
    this.analyser = null;
    this.dataArray = null;
    this.audioContext?.close().catch(console.error);
    this.audioContext = null;
  }
}
